import { BehaviorSubject } from 'rxjs';
import { CoordinatorService } from './coordinator.service';
import { Keyword } from './keyword';
import { KeywordGroup } from './keyword-group';
import { StyleGroup } from './style-group';
import { ScriptXml } from './script-xml';

export class KeywordEdit {
    private _widget?: any;

    private _source: string;
    private _weight: number;
    private _group: KeywordGroup;

    private _estimateDct?: any;

    isGroupEditMode = false;

    testTitle = '';
    testAbstract = '';

    regexp?: RegExp;
    isSubmitDisabled$ = new BehaviorSubject<boolean>(false);

    // private _originalStyle: string
    // private _originalGroupTitle: string

    constructor(
        private coords: CoordinatorService,
        private _kw: Keyword | undefined,
        private _kwGroup: KeywordGroup | undefined,
    ) {
        this._source = this._kw?.source || '';
        this._weight = this._kw?.weight || 1;
        this._group = this._kw?.group || this.coords.keyword$.getGroup('_default')!;
        this._postEstimate();
    }

    public get widget(): any {
        return this._widget;
    }
    public set widget(value: any) {
        this._widget = value;
        if (!this.regexp && this._source) {
            this.source = this._source;
        }
    }

    checkSubmitOn() {
        let val = !this.isValid;
        this.isSubmitDisabled$.next(val);
    }

    createGroup() {
        this._group = new KeywordGroup(this.coords, { title: 'Group' });
        // this.coords.keyword$.setGroup(this._group)
        this.isGroupEditMode = true;
    }

    editGroup() {
        this.isGroupEditMode = true;
    }

    get source() {
        return this._source;
    }

    set source(val: string) {
        this._source = val;
        try {
            this.regexp = this._source ? new RegExp(this._source, 'dgi') : undefined;
        } catch (e) {
            this.regexp = undefined;
        }

        this.checkSubmitOn();
        this._postEstimate();
    }

    highlight(txt: string) {
        if (!this.regexp) return txt;
        const scriptXML = new ScriptXml(this.coords);
        scriptXML.addRule({
            tag: 'kw',
            regexp: new RegExp(this.source, 'i'),
            weight: 1,
            style: this.style.css,
        });

        return scriptXML.parse(txt);
    }

    private async _postEstimate() {
        if (!this.regexp) {
            this.widget?.updateEstimate({
                preview: '—',
                main: '—',
            });
            return;
        }
        if (!this._estimateDct) {
            this._estimateDct = {
                preview: this.coords.dataset$.preview.mainXitemLs.map((itm) => [
                    itm.title,
                    itm.abstract,
                ]),
                main: this.coords.dataset$.main.mainXitemLs.map((itm) => [itm.title, itm.abstract]),
            };
        }

        const resp: any = await this.coords.worker$.autotag.call('regexp-hit-counter', {
            regexp: this.regexp,
            textDct: this._estimateDct,
        });

        this.widget?.updateEstimate(resp.counterDct);
    }

    public get group(): KeywordGroup {
        if (!this._group) {
            this._group = this.coords.keyword$.getGroup('_default')!;
            if (!this._group) {
                this._group = this.coords.keyword$.refreshDefaultGroup();
            }
        }
        return this._group!;
    }

    public set group(kg: KeywordGroup) {
        this._group = kg;
        this.checkSubmitOn();
    }

    get style() {
        return this._group.style.clone();
    }

    get hasError() {
        return !!this.source && !this.regexp;
    }

    get isValid() {
        if (!this._source.trim()) return false;
        return !this.hasError;
    }

    public get weight(): string {
        if (isNaN(this._weight)) return '';
        return this._weight.toString();
    }

    public set weight(value: string) {
        this._weight = parseInt(value);
        this.checkSubmitOn();
    }

    submit() {
        if (!this.isValid) return;
        if (this._kw && this._kw instanceof Keyword)
            this._kw.update({
                source: this._source,
                weight: this._weight,
                group: this.group,
            });
        else {
            if (this._kwGroup instanceof KeywordGroup) {
                this._kw = new Keyword(this.coords, {
                    regexp: this._source,
                    weight: this._weight,
                    groupId: this._kwGroup.id,
                });
            } else {
                this._kw = new Keyword(this.coords, {
                    regexp: this._source,
                    weight: this._weight,
                    groupId: this._group.id,
                });
            }
            this.coords.keyword$.update(this._kw);
        }

        this.coords.display$.closeDialog();
    }

    styleKwEdit(kwEdit: any) {
        if (kwEdit._kwGroup !== undefined) {
            return kwEdit._kwGroup.style.css;
        } else {
            return kwEdit.group.style.css;
        }
    }

    valueKwEdit(kwEdit: any) {
        if (kwEdit._kwGroup !== undefined) {
            return kwEdit._kwGroup.title;
        } else {
            return kwEdit.group.title;
        }
    }

    get hasDefaultGroup() {
        return this._group.id === '_default';
    }

    submitGroup(title: string, st: StyleGroup) {
        this._group.title = title;
        const grp = this.coords.keyword$.getGroup(this._group.id);
        this._group.style.updateFromStyleGroup(st);
        if (!grp) this.coords.keyword$.setGroup(this._group);
        this.isGroupEditMode = false;
        this.coords.keyword$.markForManifestStore();
    }

    deleteGroup() {
        this.group = this.group.delete();
        this.isGroupEditMode = false;
    }

    get submitTitle() {
        return this.coords.text(this._kw ? 'Update' : 'Create');
    }

    dialog() {
        // console.log("THIS", this)
        this.coords.display$.dialog$.next({
            action: 'keyword',
            kw: this,
        });
    }

    delete() {
        this._kw!.delete();
    }

    get dialogTitle() {
        return `${this.coords.text(this._kw ? 'Edit' : 'Create')} ${this.coords.text('keyword')}`;
    }

    get editGroupTitle() {
        return `${this.coords.text(this.isNewGroup ? 'Create' : 'Edit')} ${this.coords.text('group')}`;
    }

    get isNew() {
        return !this._kw;
    }

    get isNewGroup() {
        return !this.coords.keyword$.getGroup(this._group.id);
    }

    submitGroupParams(fun: () => void) {
        return {
            bLs: [
                {
                    isPrimary: true,
                    title: this.isNewGroup ? 'CreateGroup' : 'UpdateGroup',
                    fun: fun,
                },
                {
                    title: 'Close',
                    fun: () => (this.isGroupEditMode = false),
                },
            ],
        };
    }
}
